<template>
  <div class="settings-action-plan v-centerer generic-border">
    <div class="settings-action-plan__drag">
      <DraggingIndication />
    </div>
    <div class="settings-action-plan__details">
      <div class="v-centerer">
        <EditableLabel
          v-model="value.name"
          class="settings-action-plan__title"
          :placeholder="$t('message.NEW_ACTION_PLAN')"
          :legend="$t('message.NAME')"
          use-textarea
          @input="$emit('input', value)"
        />
        <EditableLabel
          v-model="value.maxActions"
          :legend="$t('message.ACTION_PLAN_MAX_ACTIONS')"
          class="settings-action-plan__max"
          :disabled="isSupport"
          use-textarea
          @input="$emit('input', value)"
        />
        <div class="settings-action-plan__duration">
          <div class="legend legend--bottom-margin">
            {{ $t('message.ACTION_PLAN_DURATION') }}
          </div>
          <div class="v-centerer">
            <EditableLabel v-model="durationNumber" use-textarea />
            <EditableLabelRadio
              v-model="durationUnit"
              :items="unitsItem"
              label-key="unit"
              generic-border
            />
          </div>
        </div>
      </div>
      <Checkbox
        v-model="isSupport"
        style="padding: 1px 0px"
        class="settings-action-plan__support"
        :title="$t('message.SUPPORT_CHECKBOX')"
        :disabled="value.nActions > 0"
        :err-msg="$t('message.CANT_EDIT_PLAN_TYPE')"
      />
    </div>

    <div class="settings-action-plan__teams h-v-centerer">
      <div
        v-if="value.usedByTeamIds"
        class="generic-border generic-border--icon-button generic-border--blue"
        @click="showTeamUsingTemplate"
      >
        <Icon>{{ teamMedium }}</Icon>
      </div>
    </div>
    <div class="settings-action-plan__delete h-v-centerer">
      <div
        class="generic-border generic-border--icon-button generic-border--red"
        :class="{ mt10: value.usedByTeamIds }"
        @click="$emit('delete', value)"
      >
        <Icon>{{ deleteMedium }}</Icon>
      </div>
    </div>


    <Modal
      class="settings-action-plan__teams-modal"
      :value="displayTeamsUsingTemplate"
      @input="displayTeamsUsingTemplate = $event"
    >
      <div>

        <h2 class="basic-sub-title">
          {{ $t('message.CONFIG_ACTION_PLAN_LIST_TEAMS') }}
        </h2>
        <div class="basic-search-field v-centerer mb10em">
          <Icon>{{ searchMedium }}</Icon>
          <input v-model="searchTemsUsingTemplate" :placeholder="$t('message.SEARCH_PH')">
        </div>

        <div
          v-for="team in teamsUsingTemplate"
          :key="team.id"
          class="small-card-item v-centerer"
        >
          <span
            class="dot"
            :style="'background-color:' + hierarchyColors[team.hierarchyLevel % hierarchyColors.length].dark"
          />
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span class="name" v-html="team.name" />
          <span class="ml10em">
            {{ $t('message.ID') + $t('message.TWO_DOTS') + team.id + '-' + team.strId }}
          </span>
        </div>
      </div>

    </Modal>
  </div>
</template>

<script>
/**
 * Settings of a single action plan.
 * This component holds the view to edit an action plans, whether it belongs to a team or a company.
 * @vue-prop {Object} value - Action plan object.
 */

import { cloneDeep } from 'lodash';
// Components
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabelRadio from '@/components/editable-label-radio.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
// Utils
import { hierarchyColors } from '@/utils/config-utils';
import { actionPlanUnits } from '@/utils/action-utils';
import { deleteMedium, moveMedium, searchMedium, teamMedium } from '@/assets/icons';
import { GET_TEAMS } from '@/store/mutation-types';

export default {

  name: 'SettingsActionPlan',

  components: {
    Checkbox,
    DraggingIndication,
    EditableLabelRadio,
    EditableLabel,
    Icon,
    Modal,
  },

  props: {
    value: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      deleteMedium,
      moveMedium,
      searchMedium,
      teamMedium,
      hierarchyColors,
      reversedUnits: cloneDeep(actionPlanUnits).reverse(),
      displayTeamsUsingTemplate: false,
      searchTemsUsingTemplate: '',
    };
  },

  computed: {
    unitsItem() {
      return actionPlanUnits.map(unit => ({
        id: unit.id,
        unit: this.$tc(unit.unit, Number(this.durationNumber)),
      }));
    },
    /** Number: number of durationUnit for the duration of an action */
    durationNumber: {
      get() {
        const unit = this.reversedUnits.find(u => this.value.actionDuration % u.nHours === 0);
        return unit ? this.value.actionDuration / unit.nHours : this.value.actionDuration;
      },
      set(duration) {
        const unit = actionPlanUnits[this.durationUnit];
        this.$emit('input', { ...this.value, actionDuration: duration * unit.nHours });
      },
    },
    /** Number: Unit ID of the duration of an action */
    durationUnit: {
      get() {
        const unit = this.reversedUnits.find(u => this.value.actionDuration % u.nHours === 0);
        return unit ? unit.id : 0;
      },
      set(unitId) {
        const unit = actionPlanUnits[unitId];
        this.$emit('input', { ...this.value, actionDuration: this.durationNumber * unit.nHours });
      },
    },
    /** Boolean:whether this plan is a support plan */
    isSupport: {
      get() {
        return this.value.support === 1;
      },
      set(isSupport) {
        this.$emit('input', { ...this.value, support: isSupport ? 1 : 0 });
      },
    },
    /** Array: Array of teams objects that use the current action plan template */
    teamsUsingTemplate() {
      if (!this.value.usedByTeamIds || !this.value.usedByTeamIds.length) {
        return [];
      }
      let stateTeams = this.$store.state[GET_TEAMS.stateKey] || [];
      stateTeams = stateTeams.filter(team => this.value.usedByTeamIds.includes(team.id));
      // Filter using search bar (search in name and id)
      const effectiveSearch = this.searchTemsUsingTemplate.toUpperCase().split(/[\s,-./']/);
      return stateTeams.filter((team) => {
        const uppperName = team.name.toUpperCase();
        const inName = !effectiveSearch.some(term => !uppperName.includes(term));
        const fullId = `${team.id}-${team.strId}`.toUpperCase();
        const inId = !effectiveSearch.some(term => !fullId.includes(term));
        return inName || inId;
      });
    },
  },

  methods: {
    /** Display which teams use this action plan */
    showTeamUsingTemplate() {
      this.displayTeamsUsingTemplate = true;
    },
  },

};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.settings-action-plan {
  padding: 1rem;

  &__drag {
    flex: 0 0 auto;
    margin-right: 1rem;
  }

  &__details {
    flex: 1;
    min-width: 0px;
  }

  &__teams {
    flex: 0 0 6rem;
  }

  &__delete {
    flex: 0 0 6rem;
  }

  &__title, &__max, &__duration {
    flex: 1;
    min-width: 0px;

    &:not(:first-child) {
      margin-left: 0.7rem;
    }
  }

  &__title {
    font-weight: 600;
  }

  &__duration {
    .editable-label {
      flex: 1;

      &:first-child {
        margin-right: .7rem;
      }
    }
  }

  &__support {
    margin-top: .7rem;
  }

  &__teams-modal {

    &.dark-overlay .dark-overlay-content {
      width: auto;
      height: auto;
      min-height: 15em;
      max-height: 60%;
    }

    .content-wrapper {
      padding: 1em 3em;
      box-sizing: border-box;

      h2.basic-sub-title {
        margin-top: 0;
      }

      .small-card-item {
        box-sizing: border-box;
        width: calc(100% - 1em);
        margin-left: 0.5em;
        margin-right: 0.5em;

        &:hover {
          background: none;
        }

        > * {
          flex: 0 0 auto;

          &.name {
            flex: 1;
          }
        }
      }
    }
  }
}
</style>
